import { MAX_PERIOD_DAYS } from '~utils/date-picker-helpers';
import type { ApiPeriod } from '~containers/analytics/kpis/periods';
import style from './style.module.scss';

/**
 * Display order: Campaign, then the windows narrowest first.
 *
 * Campaign leads: on a campaign dashboard it is the period a reader wants far
 * more often than a trailing window, and it is the only one whose bounds are
 * specific to this page.
 */
const PRESETS: readonly { id: ApiPeriod; label: string }[] = [
    { id: 'campaign', label: 'Campaign' },
    { id: 'today', label: 'Today' },
    { id: '7d', label: 'Last 7 days' },
    { id: '30d', label: 'Last 30 days' },
    { id: '90d', label: 'Last 90 days' },
];

const SHORTENED_LABEL = `Campaign (last ${MAX_PERIOD_DAYS} days)`;

const SHORTENED_TITLE =
    `This campaign is longer than the ${MAX_PERIOD_DAYS}-day maximum period, ` +
    `so this selects its last ${MAX_PERIOD_DAYS} days. Totals to date still span the whole campaign.`;

export interface PeriodPresetsProps {
    /** The preset the page is on, absent for a hand-picked range. */
    active: ApiPeriod | undefined;
    onSelect: (preset: ApiPeriod) => void;
    /** Off on a dashboard that is not a campaign, which drops the capsule. */
    hasCampaign?: boolean;
    /** The `Campaign` capsule selects only the campaign's last days. */
    campaignShortened?: boolean;
    /** `segmented` joins the shortcuts into one bordered control the height
     * of the date picker, for the single-row filter bar. */
    appearance?: 'capsules' | 'segmented';
}

/**
 * Quick-select capsules for the period filter. Each names a preset the API
 * resolves; the selected one lives in the URL as `period`.
 */
export const PeriodPresets = ({
    active,
    onSelect,
    hasCampaign = false,
    campaignShortened = false,
    appearance = 'capsules',
}: PeriodPresetsProps) => (
    <div
        className={appearance === 'segmented' ? style.segmented : style.presets}
        role="group"
        aria-label="Period shortcuts"
    >
        {PRESETS.map(({ id, label }) => {
            if (id === 'campaign' && !hasCampaign) return null;
            const shortened = id === 'campaign' && campaignShortened;
            const pressed = active === id;
            return (
                <button
                    key={id}
                    type="button"
                    aria-pressed={pressed}
                    title={shortened ? SHORTENED_TITLE : undefined}
                    className={
                        appearance === 'segmented'
                            ? `${style.segment} ${
                                  pressed ? style.segment_active : ''
                              }`
                            : `${style.capsule} ${
                                  pressed ? style.capsule_active : ''
                              }`
                    }
                    onClick={() => onSelect(id)}
                >
                    {shortened ? SHORTENED_LABEL : label}
                </button>
            );
        })}
    </div>
);
